import React, { useEffect, useMemo, useState, useRef } from "react";
import { LeagueContext, type League } from "./LeagueContext";
import useStateWithLocalStorage from "../hooks/useStateWithLocalStorage";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "./AuthContext";
import { API_ENDPOINTS } from "../config/api";
import { PaginatedResponse } from "../types/api";
import { ApiError } from "../utils/apiClient";

function LeagueProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [currentLeague, setCurrentLeague] =
    useStateWithLocalStorage<League | null>("selectedLeague", null);
  const [selectedSeasonId, setSelectedSeasonId] = useStateWithLocalStorage<
    number | null
  >("selectedSeasonId", null);
  const [refreshKey, setRefreshKey] = useState(0);
  const previousLeagueIdRef = useRef<number | null>(null);

  // Fetch leagues from API
  const url = auth.isAuthenticated()
    ? `${API_ENDPOINTS.leagues}?refresh=${refreshKey}`
    : undefined;
  const { data: response, error } = useRequest<PaginatedResponse<League>>(
    url,
    auth.token,
  );

  // A stored token the API rejects is a dead session. This request runs before
  // the router mounts SessionTimeoutHandler, so nothing else would sign the
  // user out, and an empty league list would read as "needs setup".
  useEffect(() => {
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      auth.isAuthenticated()
    ) {
      auth.logout(() => {});
    }
  }, [error, auth]);

  const leagues = useMemo(() => response?.data || [], [response]);
  const isLoading = !response && !error && auth.isAuthenticated();

  // Sync currentLeague with fresh API data to update localStorage
  // Also clear if the league no longer exists
  useEffect(() => {
    if (leagues.length > 0 && currentLeague) {
      // Find the matching league from fresh API data
      const freshLeague = leagues.find((l) => l.id === currentLeague.id);
      if (freshLeague) {
        // Check if the league data has changed (deep comparison of relevant fields)
        const hasChanged =
          freshLeague.name !== currentLeague.name ||
          freshLeague.abbr !== currentLeague.abbr ||
          freshLeague.active_season_id !== currentLeague.active_season_id ||
          freshLeague.updated_at !== currentLeague.updated_at;

        if (hasChanged) {
          // Update with fresh data to sync localStorage
          setCurrentLeague(freshLeague);
        }
      } else {
        // League no longer exists in API response - clear it
        console.log(
          "[LeagueProvider] Selected league no longer exists, clearing",
          currentLeague.id,
        );
        setCurrentLeague(null);
        setSelectedSeasonId(null);
      }
    }
  }, [leagues, currentLeague, setCurrentLeague, setSelectedSeasonId]);

  // Auto-select first league if only one exists and none is selected
  useEffect(() => {
    if (leagues.length > 0 && !currentLeague) {
      setCurrentLeague(leagues[0]);
    }
  }, [leagues, currentLeague, setCurrentLeague]);

  // Reset selectedSeasonId when league changes
  useEffect(() => {
    if (!currentLeague) {
      previousLeagueIdRef.current = null;
      return;
    }

    // If league has changed, reset the selected season
    if (
      previousLeagueIdRef.current !== null &&
      previousLeagueIdRef.current !== currentLeague.id
    ) {
      setSelectedSeasonId(null);
    }

    // Update the ref to current league ID
    previousLeagueIdRef.current = currentLeague.id;
  }, [currentLeague, setSelectedSeasonId]);

  // Initialize selectedSeasonId with active_season_id if not already set
  useEffect(() => {
    console.log("[LeagueProvider] Season initialization check", {
      hasCurrentLeague: !!currentLeague,
      currentLeagueId: currentLeague?.id,
      activeSeason: currentLeague?.active_season_id,
      selectedSeasonId,
    });

    if (!currentLeague) return;

    const activeSeason = currentLeague.active_season_id;

    // Only initialize if no season is selected yet
    // Handle both null and undefined (in case of partial API responses)
    if (selectedSeasonId === null && activeSeason != null) {
      console.log(
        "[LeagueProvider] Initializing selectedSeasonId to",
        activeSeason,
      );
      setSelectedSeasonId(activeSeason);
    }
  }, [currentLeague, selectedSeasonId, setSelectedSeasonId]);

  const refreshLeagues = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const value = {
    currentLeague,
    setCurrentLeague,
    selectedSeasonId,
    setSelectedSeasonId,
    leagues,
    refreshLeagues,
    isLoading,
  };

  return (
    <LeagueContext.Provider value={value}>{children}</LeagueContext.Provider>
  );
}

export default LeagueProvider;
